import Chart from 'chart.js';
import { motionCount } from '../../../Foundation/motion';

export class SavingsAndEarnings {
    /**
    * Creates a new instance of a SavingsAndEarnings component
    * @param {HTMLElement} element SavingsAndEarnings DOM element
    */
    constructor(element) {
        this.el = element;
		this._refs = {
			loading: this.el.querySelector('.savings-and-earnings__loading'),
			noDataSections: this.el.querySelectorAll('.savings-and-earnings__no-data'),
			datasetTriggers: this.el.querySelectorAll('[data-dataset-trigger]'),
			chartsSection: this.el.querySelector('.savings-and-earnings__charts'),
			gainsSection: this.el.querySelector('.savings-and-earnings__gains'),
			sectionsWrapper: this.el.querySelector('.savings-and-earnings__sections'),
			chartWrappers: this.el.querySelectorAll('.savings-and-earnings__chart-wrapper')
        };
		this._props = {
			results: [],
			chartsCanvas: [],
			numberFormatter: undefined
		};
		this.config = this.parseConfiguration();

		if (!this.config) return;

        this.init();

		// Events
		this._refs.datasetTriggers.forEach(datasetTrigger => {
			datasetTrigger.addEventListener('click', () => this.onDatasetChange(datasetTrigger));
		});
	}

	parseConfiguration() {
		try {
			return JSON.parse(this.el.dataset.savingsAndEarningsConfig);
		} catch (e) {
			console.error('Savings and Earnings: configuration to parse is not valid JSON!', e);
		}
	}

	init() {
		this.initCharts();
		this.toggleSectionsVisibility(0);
		this._props.numberFormatter = this.createNumberFormatter();
		setTimeout(() => this.hideLoading(), 600);
	}

	createNumberFormatter() {
		const { cultureInfoName } = this.config;
		return new Intl.NumberFormat(cultureInfoName, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
	}

	show(el) {
		el.classList.remove('is-hidden');
	}

	hide(el) {
		el.classList.add('is-hidden');
	}

	hideLoading() {
		const { loading } = this._refs;
		this.hide(loading);
	}

	toggleSectionsVisibility(datasetToShow) {
		const { chartsSection, sectionsWrapper } = this._refs;
		const sections = sectionsWrapper.querySelectorAll('.savings-and-earnings__section');
		const sectionsVisibility = [];

		sections.forEach((section) => {
			const chart = section.querySelector('.chart');
			const datasetFormattedSums = this.getDatasetFormattedSums(chart);
			const total = this.getDatasetFormattedSum(datasetFormattedSums, datasetToShow);
			sectionsVisibility.push(total === '0' ? true : false);
		});

		const allSectionsHidden = sectionsVisibility.every(s => s);
		const anySectionHidden = sectionsVisibility.some(s => s);

		if (allSectionsHidden) {
			this.showNoDataSection(datasetToShow);
		} else if (anySectionHidden) {
			sections.forEach((section, index) => {
				if (sectionsVisibility[index]) {
					this.hide(section);
				} else {
					this.show(section);
				}

				if (index > 0) {
					section.classList.remove('savings-and-earnings__separator');
				}
			});

			chartsSection.classList.add('savings-and-earnings__charts--narrow');
			sectionsWrapper.classList.add('savings-and-earnings__sections--narrow');
			this.hideNoDataSections();
		} else {
			sections.forEach((section, index) => {
				this.show(section);

				if (index > 0) {
					section.classList.add('savings-and-earnings__separator');
				}
			});

			chartsSection.classList.remove('savings-and-earnings__charts--narrow');
			sectionsWrapper.classList.remove('savings-and-earnings__sections--narrow');
			this.hideNoDataSections();
		}
	}

	showNoDataSection(datasetToShow) {
		const { chartsSection, gainsSection, noDataSections } = this._refs;
		const indexToShow = noDataSections[datasetToShow] ? datasetToShow : 0;
		this.hide(chartsSection);
		this.hide(gainsSection);

		noDataSections.forEach((noDataSection, index) => {
			if (indexToShow == index) {
				this.show(noDataSection);
			} else {
				this.hide(noDataSection);
			}
		})
	}

	hideNoDataSections() {
		const { chartsSection, gainsSection, noDataSections } = this._refs;
		noDataSections.forEach(section => this.hide(section));
		this.show(chartsSection);
		this.show(gainsSection);
	}

	getDatasetFormattedSum(datasetFormattedSums, index) {
		return datasetFormattedSums[index] ?? '0';
	}

	initCharts() {
		const { chartWrappers } = this._refs;
		const { chartsCanvas } = this._props;

		chartWrappers.forEach(chartWrapper => {
			// Grab everything we need
			// The chart canvas
			const chart = chartWrapper.querySelector('.chart');
			// All the labels
			const datasetLabels = this.getDatasetLabels(chart);
			// All the values
			const datasetValues = this.getDatasetValues(chart);
			// All the formatted sums
			const datasetFormattedSums = this.getDatasetFormattedSums(chart);
			// Element to contain the totaled / summed up values
			const total = chartWrapper.querySelector('[data-dataset-total]');
			// Colors used on the chart and legend
			const colors = this.getDatasetColors(chart);

			// Combine arrays taking 1st value from each, then 2nd, then 3rd etc
			// e.g
			// [[100, 200, 20, 1], [50, 25, 12, 5]]
			// would become...
			// [[100,50],[200,25],[20,12],[1,5]]
			// This is then the data points for each chart
			// And the triggers just change the index being called to switch the data, see below
			// So 'result' is the output of all the data we are sent to use
			const results = this.mergedArrays(datasetValues);
			this._props.results.push(results);

			// On page load upate the sum / total
			total.textContent = this.getDatasetFormattedSum(datasetFormattedSums, 0);

			// Chart data setup
			const data = {
				labels: datasetLabels,
				datasets: [
					{
						data: results[0],
						backgroundColor: colors,
						hoverBackgroundColor: colors,
						borderWidth: 0,
						cutout: '89%'
					}
				]
			};

			// Initialise a new chart
			const chartCanvas = new Chart(chart, {
				type: 'doughnut',
				data,
				options: this.getChartDefaultOptions()
			});

			chartsCanvas.push(chartCanvas);
		});
	}

	mergedArrays(arrays) {
		if (!arrays) return [];
		const length = arrays[0]?.length ?? 0;
		return Array.from({ length }, (_, index) =>
			arrays.map(arr => arr[index])
		);
	}

	getChartAttributes(chart) {
		return Array.from(chart.attributes);
	}

	getDatasetLabels(chart) {
		return this.getChartAttributes(chart)
			.filter((attr) => attr.name.startsWith('data-dataset-label'))
			.map((attr) => attr.value);
	}

	getDatasetColors(chart) {
		return this.getChartAttributes(chart)
			.filter((attr) => attr.name.startsWith('data-dataset-color'))
			.map((attr) => attr.value);
	}

	getDatasetValues(chart) {
		const chartAttributes = this.getChartAttributes(chart);
		const datasetValues = [];

		this.getDatasetLabels(chart).forEach((label, i) => {
			const datasetValueAttr = chartAttributes.find(attr => attr.name === `data-dataset-values-${i}`);

			if (datasetValueAttr) {
				const values = datasetValueAttr.value.split(',').map(Number);
				datasetValues.push(values);
			}
		});

		return datasetValues;
	}

	getDatasetSums(chart) {
		const { datasetTriggers } = this._refs;
		const chartAttributes = this.getChartAttributes(chart);
		const datasetSums = [];

		datasetTriggers.forEach((datasetTrigger, i) => {
			const datasetSumAttr = chartAttributes.find(attr => attr.name === `data-dataset-values-sum-${i}`);
			const sum = datasetSumAttr ? +datasetSumAttr.value : 0;
			datasetSums.push(sum);
		});

		return datasetSums;
	}

	getDatasetFormattedValues(chart) {
		return this.getChartAttributes(chart)
			.filter(attr => attr.name.startsWith('data-dataset-values-formatted'))
			.map(attr => attr.value.split('+'));
	}

	getDatasetFormattedSums(chart) {
		return this.getChartAttributes(chart)
			.filter(attr => attr.name.startsWith('data-dataset-values-sum-formatted'))
			.map(attr => attr.value);
	}

	onDatasetChange(datasetTrigger) {
		const { chartWrappers, datasetTriggers } = this._refs;
		const { chartsCanvas, results } = this._props;
		const datasetToShow = datasetTrigger.dataset.datasetTrigger;

		// Update triggers / buttons
		datasetTriggers.forEach(trigger => trigger.classList.remove('active'));
		datasetTrigger.classList.add('active');

		this.toggleSectionsVisibility(datasetToShow);

		// Update charts / canvas
		chartsCanvas.forEach((chartCanvas, i) => {
			chartCanvas.data.datasets[0].data = results[i][`${datasetToShow}`];
			chartCanvas.update();
		});

		// Update data in table next to chart
		chartWrappers.forEach(chartWrapper => this.updateTableData(chartWrapper, datasetToShow));

		this.updateFinancialGainsValue(datasetToShow);
	}

	updateTableData(chartWrapper, datasetToShow) {
		const chart = chartWrapper.querySelector('.chart');
		const datasetFormattedValues = this.getDatasetFormattedValues(chart);
		const datasetFormattedSums = this.getDatasetFormattedSums(chart);
		// Tabular data next to each chart
		const tableData = chartWrapper.parentNode.querySelectorAll('[data-dataset-value]');

		tableData.forEach((data, i) => {
			const formattedValues = datasetFormattedValues[i];
			if (formattedValues) {
				data.textContent = formattedValues[`${datasetToShow}`] ?? '0';
			} else {
				data.textContent = '0';
			}
		});

		// Element to contain the totaled / summed up values
		const total = chartWrapper.querySelector('[data-dataset-total]');
		// Update sum
		total.textContent = this.getDatasetFormattedSum(datasetFormattedSums, `${datasetToShow}`);
	}

	updateFinancialGainsValue(datasetToShow) {
		const { chartWrappers, gainsSection } = this._refs;
		const { currencySymbol } = this.config;
		const { numberFormatter } = this._props;
		const cardDataEl = gainsSection.querySelector('.card__data');

		const sum = [...chartWrappers].reduce((acc, chartWrapper) => {
			const chart = chartWrapper.querySelector('.chart');
			const chartSums = this.getDatasetSums(chart);
			return acc + chartSums[datasetToShow];
		}, 0);

		if (cardDataEl) {
			const formattedSum = numberFormatter.format(sum);
			cardDataEl.dataset.end = `${currencySymbol}${formattedSum}`;
			motionCount(cardDataEl);
		}
	}

	getChartDefaultOptions() {
		return {
			animation: {
				duration: 2000,
				delay: 0
			},
			plugins: {
				legend: {
					display: false
				},
				title: {
					text: '',
					display: false
				},
				tooltip: {
					enabled: false, // Temp hidden, it causes a lot of issue with long labels - will need design if we want to show them
				},
				hover: {
					mode: null
				}
			}
		};
	}
}